import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatTile } from '@/components/ui/stat-tile';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  CASH_MOVEMENT_KIND_LABEL,
  CASH_MOVEMENT_KIND_LABEL_KEY,
  SHIFT_STATE_LABEL,
  SHIFT_STATE_LABEL_KEY,
  formatMinor,
  useReconcileShift,
  useShift,
  type CashCountRecord,
  type CashMovementRecord,
  type ShiftDetail,
} from '@/hooks/use-shifts';
import { useT } from '@/i18n/use-t';

export function ShiftDetailPage() {
  const t = useT('admin_ui');
  const { id } = useParams<{ id: string }>();
  const shiftQuery = useShift(id);
  const reconcile = useReconcileShift();

  if (id === undefined) return null;
  if (shiftQuery.isLoading)
    return (
      <p className="text-sm text-muted-foreground">
        {t('shifts.detail.loading', 'Loading shift…')}
      </p>
    );
  const shift = shiftQuery.data;
  if (shift === undefined)
    return (
      <p className="text-sm text-destructive">{t('shifts.detail.notFound', 'Shift not found.')}</p>
    );

  return (
    <div className="max-w-4xl space-y-5">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground">
            <Link to="/shifts" className="hover:underline">
              {t('shifts.list.title', 'Shifts')}
            </Link>{' '}
            / <span className="font-mono">{shift.id.slice(0, 12)}…</span>
          </p>
          <h1 className="mt-1 text-2xl font-semibold">
            {shift.cashier.displayName} — {shift.terminal.code}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('shifts.detail.openedAt', 'Opened {when}').replace(
              '{when}',
              formatDateTime(shift.openedAt),
            )}
            {shift.closedAt !== null &&
              ` · ${t('shifts.detail.closedAt', 'Closed {when}').replace(
                '{when}',
                formatDateTime(shift.closedAt),
              )}`}
          </p>
        </div>
        <Badge variant={shift.state === 'reconciled' ? 'success' : 'outline'}>
          {t(SHIFT_STATE_LABEL_KEY[shift.state], SHIFT_STATE_LABEL[shift.state])}
        </Badge>
      </header>

      {/* ---------- Cash summary ---------- */}

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <StatTile
          label={t('shifts.detail.openingCash', 'Opening cash')}
          value={formatMinor(shift.openingCashMinor, shift.currencyCode)}
        />
        <StatTile
          label={t('shifts.detail.closingCounted', 'Closing (counted)')}
          value={formatMinor(shift.closingCashCountedMinor, shift.currencyCode)}
        />
        <StatTile
          label={t('shifts.detail.closingExpected', 'Closing (expected)')}
          value={formatMinor(shift.closingCashExpectedMinor, shift.currencyCode)}
        />
        <StatTile
          label={t('shifts.field.variance', 'Variance')}
          value={formatMinor(shift.varianceMinor, shift.currencyCode)}
          highlight
        />
      </section>

      {/* ---------- Reconciliation form ---------- */}

      {(shift.state === 'closed' || shift.state === 'abnormal_close') && (
        <ReconcileForm
          shift={shift}
          isPending={reconcile.isPending}
          onSubmit={async (body) => {
            await reconcile.mutateAsync({ id: shift.id, body });
          }}
        />
      )}

      {shift.reconciliationAdjustments.length > 0 && (
        <section className="space-y-2 rounded-card border border-border bg-card p-5">
          <h2 className="text-sm font-medium">
            {t('shifts.detail.adjustments', 'Reconciliation adjustments')}
          </h2>
          <Table>
            <THead>
              <TR>
                <TH>{t('shifts.field.when', 'When')}</TH>
                <TH className="text-right">{t('shifts.field.amount', 'Amount')}</TH>
                <TH>{t('shifts.field.reason', 'Reason')}</TH>
              </TR>
            </THead>
            <TBody>
              {shift.reconciliationAdjustments.map((adj) => (
                <TR key={adj.id}>
                  <TD className="text-xs">{formatDateTime(adj.createdAt)}</TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(adj.amountMinor, shift.currencyCode)}
                  </TD>
                  <TD className="text-sm">{adj.reason}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </section>
      )}

      {/* ---------- Cash movement ledger ---------- */}

      <section className="space-y-2 rounded-card border border-border bg-card p-5">
        <h2 className="text-sm font-medium">
          {t('shifts.movements.title', 'Cash movements')} ({shift.cashMovements.length})
        </h2>
        {shift.cashMovements.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('shifts.detail.noMovements', 'No cash movements recorded.')}
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('shifts.field.when', 'When')}</TH>
                <TH>{t('shifts.field.kind', 'Kind')}</TH>
                <TH className="text-right">{t('shifts.field.amount', 'Amount')}</TH>
                <TH>{t('shifts.field.reason', 'Reason')}</TH>
                <TH>{t('shifts.field.notes', 'Notes')}</TH>
              </TR>
            </THead>
            <TBody>
              {shift.cashMovements.map((m: CashMovementRecord) => (
                <TR key={m.id}>
                  <TD className="text-xs">{formatDateTime(m.occurredAt)}</TD>
                  <TD>
                    <Badge variant="outline">
                      {t(CASH_MOVEMENT_KIND_LABEL_KEY[m.kind], CASH_MOVEMENT_KIND_LABEL[m.kind])}
                    </Badge>
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(m.amountMinor, shift.currencyCode)}
                  </TD>
                  <TD className="text-sm">{m.reasonCode}</TD>
                  <TD className="text-xs text-muted-foreground">{m.notes ?? '—'}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </section>

      {/* ---------- Drawer counts (VL-250) ---------- */}

      {(shift.cashCounts?.length ?? 0) > 0 && (
        <section
          className="space-y-2 rounded-card border border-border bg-card p-5"
          data-testid="shift-cash-counts"
        >
          <h2 className="text-sm font-medium">
            {t('shifts.counts.title', 'Drawer counts')} ({shift.cashCounts?.length ?? 0})
          </h2>
          <Table>
            <THead>
              <TR>
                <TH>{t('shifts.field.when', 'When')}</TH>
                <TH className="text-right">{t('shifts.counts.expected', 'Expected')}</TH>
                <TH className="text-right">{t('shifts.counts.counted', 'Counted')}</TH>
                <TH className="text-right">{t('shifts.field.variance', 'Variance')}</TH>
                <TH>{t('shifts.counts.denominations', 'By denomination')}</TH>
              </TR>
            </THead>
            <TBody>
              {(shift.cashCounts ?? []).map((c: CashCountRecord) => (
                <TR key={c.id}>
                  <TD className="text-xs">{formatDateTime(c.countedAt)}</TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(c.expectedMinor, c.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(c.countedMinor, c.currency)}
                  </TD>
                  <TD
                    className={
                      c.differenceMinor === '0'
                        ? 'text-right tabular-nums'
                        : 'text-right tabular-nums text-destructive'
                    }
                  >
                    {formatMinor(c.differenceMinor, c.currency)}
                  </TD>
                  <TD className="text-xs text-muted-foreground">
                    {c.denominations
                      .filter((line) => line.count > 0)
                      .map(
                        (line) =>
                          `${formatMinor(line.denominationMinor, c.currency)} × ${line.count}`,
                      )
                      .join(', ') || '—'}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </section>
      )}

      {shift.zReportRef !== null && (
        <p className="text-xs text-muted-foreground">
          {t('shifts.detail.zReportRef', 'Z-report ref:')}{' '}
          <code className="font-mono">{shift.zReportRef}</code>
        </p>
      )}
    </div>
  );
}

type ReconcileBody =
  | { expectedAction: 'accept'; reason: string }
  | { expectedAction: 'adjust'; adjustmentMinor: string; reason: string };

function ReconcileForm({
  shift,
  isPending,
  onSubmit,
}: {
  shift: ShiftDetail;
  isPending: boolean;
  onSubmit: (body: ReconcileBody) => Promise<void>;
}) {
  const t = useT('admin_ui');
  const [mode, setMode] = useState<'accept' | 'adjust'>('accept');
  const [adjustment, setAdjustment] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setError(null);
    if (reason.trim().length === 0) {
      setError(t('shifts.detail.reasonRequired', 'Reason is required.'));
      return;
    }
    try {
      if (mode === 'accept') {
        await onSubmit({ expectedAction: 'accept', reason });
      } else {
        const cents = Math.round(Number(adjustment) * 100);
        if (!Number.isFinite(cents)) {
          setError(
            t(
              'shifts.detail.adjustmentInvalid',
              'Adjustment must be a number (positive or negative, in major units).',
            ),
          );
          return;
        }
        await onSubmit({
          expectedAction: 'adjust',
          adjustmentMinor: cents.toString(),
          reason,
        });
      }
      setReason('');
      setAdjustment('');
    } catch (err) {
      setError(extractError(err));
    }
  }

  return (
    <section className="space-y-3 rounded-card border border-border bg-card p-5">
      <h2 className="text-sm font-medium">
        {t('shifts.detail.reconcileTitle', 'Reconcile shift')}
      </h2>
      <div className="flex items-center gap-1 rounded-md border border-border bg-secondary p-1 text-sm">
        <button
          type="button"
          className={
            mode === 'accept'
              ? 'rounded bg-primary px-3 py-1 text-primary-foreground'
              : 'rounded px-3 py-1'
          }
          onClick={() => setMode('accept')}
        >
          {t('shifts.detail.acceptCounted', 'Accept counted total')}
        </button>
        <button
          type="button"
          className={
            mode === 'adjust'
              ? 'rounded bg-primary px-3 py-1 text-primary-foreground'
              : 'rounded px-3 py-1'
          }
          onClick={() => setMode('adjust')}
        >
          {t('shifts.detail.adjust', 'Adjust')}
        </button>
      </div>

      {mode === 'adjust' && (
        <div className="space-y-2">
          <Label htmlFor="adjustment">
            {t('shifts.detail.adjustmentLabel', 'Adjustment (major units; signed, e.g. -5.00)')}
          </Label>
          <Input
            id="adjustment"
            value={adjustment}
            onChange={(e) => setAdjustment(e.target.value)}
            placeholder="e.g. -5.00"
            inputMode="decimal"
            className="max-w-xs"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="reason">{t('shifts.field.reason', 'Reason')}</Label>
        <Textarea
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder={t(
            'shifts.detail.reasonPlaceholder',
            'e.g. Counted again at safe; -€5.00 returned.',
          )}
        />
      </div>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button disabled={isPending || reason.trim().length === 0}>
            {t('shifts.detail.reconcileButton', "Reconcile {name}'s shift").replace(
              '{name}',
              shift.cashier.displayName,
            )}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('shifts.detail.confirmTitle', 'Confirm reconciliation')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {mode === 'accept'
                ? t(
                    'shifts.detail.confirmAccept',
                    'Marks the shift RECONCILED without an adjustment entry.',
                  )
                : t(
                    'shifts.detail.confirmAdjust',
                    'Inserts an adjustment of {amount} and marks the shift RECONCILED.',
                  ).replace('{amount}', adjustment || '0')}{' '}
              {t(
                'shifts.detail.confirmNote',
                'This action is logged and the adjustment ledger is append-only.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={submit}>
              {t('shifts.detail.reconcile', 'Reconcile')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function extractError(err: unknown): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (Array.isArray(data?.message)) return data.message.join('; ');
    return err.message;
  }
  return 'Unexpected error';
}
